// -------------------------------------------------------------------------------
// Libraries
// -------------------------------------------------------------------------------
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// -------------------------------------------------------------------------------
// Component, selectors and others
// -------------------------------------------------------------------------------
import { Heading2, Paragraph12 } from "themes/default/_typography";
import { MembershipSelection } from "components/Payments/MembershipSelection";
import { LoadingMask } from "components/Common/LoadingMask";
import { MembershipSubscriptionDescription } from "components/Payments/MembershipSubscriptionDescription";
import { selectSelectedProduct } from "store/selectors";

// -------------------------------------------------------------------------------
// Utils, Styles and Assets
// -------------------------------------------------------------------------------
import StyledMembershipSettingsPage from "./styles";
import { updateSelectedProduct, updateCurrentMembershipDetails } from "store/slices";
import { useGetDomainGymQuery, useStripeGetGymProductQuery } from "store/api/api";
import { paymentLevels } from "helpers/constants";

// -------------------------------------------------------------------------------
// Component
// -------------------------------------------------------------------------------
const MembershipSettingsPage = ({ subscription }) => {
	const dispatch = useDispatch();
	const { gym } = useParams();
	const selectedProduct = useSelector(selectSelectedProduct);
	const { data: domainGymData, isLoading: isDomainGymDataLoading } = useGetDomainGymQuery(gym);
	const getGymProductResponse = useStripeGetGymProductQuery({ gymId: domainGymData?.id }, { skip: !domainGymData?.id });
	const [currentPricing, setCurrentPricing] = useState(undefined);
	const [planDetails, setPlanDetails] = useState();
	const [gymProduct, setGymProduct] = useState();

	useEffect(() => {
		if (subscription) {
			const planDetails = paymentLevels[subscription?.plan?.product?.name];
			setPlanDetails(planDetails);
		}
	}, [subscription]);

	useEffect(() => {
		if (gymProduct) {
			const membershipDetails = {
				id: gymProduct.id,
				product: gymProduct.id,
				unit_amount: gymProduct.unit_amount,
				type: gymProduct.type,
				membersPay: gymProduct.unit_amount / 100,
				default_price: gymProduct.default_price,
			};
			const product = {
				name: "Pro",
				price: "$99.00",
				type: "pro",
			};

			setCurrentPricing(gymProduct.unit_amount);
			dispatch(updateSelectedProduct({ product }));
			dispatch(updateCurrentMembershipDetails({ currentMembershipDetails: membershipDetails }));
		}
	}, [gymProduct, dispatch]);

	useEffect(() => {
		if (gymProduct && currentPricing !== gymProduct.unit_amount && currentPricing !== undefined) {
			getGymProductResponse.refetch();
		}
	}, [currentPricing, gymProduct, subscription]);

	useEffect(() => {
		if (getGymProductResponse.isSuccess && getGymProductResponse?.data?.id) {
			setGymProduct(getGymProductResponse.data);
		}
	}, [getGymProductResponse]);

	if (getGymProductResponse.isLoading || isDomainGymDataLoading) {
		return <LoadingMask />;
	}

	return (
		<StyledMembershipSettingsPage className="sb-membership-settings-page">
			<div className="sb-membership-settings-page__inner">
				<div className="sb-membership-settings-page__inner__heading">
					<Heading2>Membership Settings</Heading2>
					<Paragraph12>
						Membership is what your members pay to access your content. Workouts can be set to free or membership based.
					</Paragraph12>
				</div>
				{selectedProduct && planDetails && (
					<div className="sb-membership-settings-page__inner__content">
						<MembershipSelection
							product={selectedProduct}
							planDetails={planDetails}
							currentPricing={currentPricing}
							setCurrentPricing={setCurrentPricing}
						/>
						<MembershipSubscriptionDescription />
					</div>
				)}
				{!selectedProduct && planDetails && (
					<MembershipSelection
						product={{
							name: "Pro",
							price: "$99.00",
							type: "pro",
						}}
						planDetails={planDetails}
						currentPricing={currentPricing}
						setCurrentPricing={setCurrentPricing}
					/>
				)}
				{!planDetails && <div>Please complete Step 1 and subscribe to a 'SWEATBASE PLAN' first</div>}
			</div>
		</StyledMembershipSettingsPage>
	);
};

export default MembershipSettingsPage;

